import React, { useEffect, useState } from "react";
import { avanzoApi } from "@/lib/avanzoApi";

export default function AvanzoFamily() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    avanzoApi
      .parentChildren()
      .then((r) => setData(r.data))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="avz-empty">Cargando…</div>;
  if (error) return <div className="avz-empty" data-testid="avz-family-error">Esta sección es sólo para padres/madres.</div>;
  if (!data || (data.children || []).length === 0) {
    return (
      <div className="avz-card" data-testid="avz-family-empty">
        <h1 className="avz-h1">Familia</h1>
        <p className="avz-lead">Aún no hay estudiantes vinculados a tu cuenta.</p>
      </div>
    );
  }

  return (
    <div data-testid="avz-family">
      <section className="avz-card">
        <h1 className="avz-h1">Familia</h1>
        <p className="avz-lead">
          Aquí puedes seguir el avance de la preparación de tus hijos hacia AVANZO.
        </p>
      </section>

      <div className="avz-grid avz-mt-4">
        {data.children.map((c, idx) => (
          <div className="avz-card" key={idx} data-testid={`avz-family-child-${idx}`}>
            <div className="avz-row-between">
              <div>
                <h2 className="avz-h2" data-testid={`avz-family-child-name-${idx}`}>
                  {c.student_name}
                </h2>
                <p className="avz-mute">Grado: {c.grade || "—"}</p>
              </div>
              <span className="avz-chip avz-chip-good">{c.overall_progress_label}</span>
            </div>

            <div className="avz-grid avz-grid-2 avz-mt-4">
              <div>
                <div className="avz-mute">Sesiones realizadas</div>
                <div className="avz-h2">{c.sessions_completed}</div>
              </div>
              <div>
                <div className="avz-mute">Faltan para AVANZO</div>
                <div className="avz-h2">{c.days_until_avanzo} días</div>
              </div>
            </div>

            <h3 className="avz-h3 avz-mt-8">Progreso por materia</h3>
            <div className="avz-stack avz-mt-4">
              {c.subjects.map((s) => (
                <div
                  className="avz-skill-row"
                  key={s.key}
                  data-subject={s.key}
                  style={{
                    borderLeft: `4px solid var(--avz-subject-${s.key})`,
                    paddingLeft: 12,
                  }}
                >
                  <div>
                    <div className="avz-strong" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        className="avz-subject-dot"
                        aria-hidden="true"
                        style={{ background: `var(--avz-subject-${s.key})` }}
                      />
                      {s.label}
                    </div>
                    <div className="avz-mute">{s.skills_practiced} habilidades practicadas</div>
                  </div>
                  <span className="avz-chip">{s.mastery_label}</span>
                </div>
              ))}
            </div>

            <EvaluationResult title="Control de progreso" testid={`avz-family-pc-${idx}`}
              result={c.progress_check_last} labels={c.subjects} />
            <EvaluationResult title="Simulacro 1" testid={`avz-family-s1-${idx}`}
              result={c.simulation_1_last} labels={c.subjects} />
            <EvaluationResult title="Simulacro Final" testid={`avz-family-sf-${idx}`}
              result={c.final_simulation_last} labels={c.subjects} />

            <p className="avz-mute avz-mt-8">
              {parentSummary(c)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("es-SV", { dateStyle: "medium" }) : "");

function EvaluationResult({ title, result, labels, testid }) {
  if (!result) return null;
  const labelOf = (k) => (labels || []).find((s) => s.key === k || s.label === k)?.label || k;
  const pct = result.percentage != null ? Math.round(result.percentage) : null;
  const band = result.band_label || result.band?.label;
  const subjects = Object.entries(result.subject_results || {});
  const strong = result.areas_of_strength || [];
  const review = result.areas_to_reinforce || (result.areas_to_review || []).map(labelOf);
  return (
    <div className="avz-mt-8" data-testid={testid}>
      <div className="avz-row-between">
        <h3 className="avz-h3">{title}</h3>
        <span className="avz-mute">{fmtDate(result.completed_at)}</span>
      </div>
      <div className="avz-mt-4">
        {pct != null && <span className="avz-h2" data-testid={`${testid}-pct`}>{pct}%</span>}
        {result.total_items ? <span className="avz-mute"> · {result.total_correct} de {result.total_items} correctas</span> : null}
        {band && <div className="avz-mute">{band}</div>}
      </div>
      {subjects.length > 0 && (
        <div className="avz-stack avz-mt-4">
          {subjects.map(([k, r]) => (
            <div className="avz-skill-row" key={k}>
              <span>{labelOf(k)}</span>
              <span className="avz-chip">{r?.percentage != null ? `${Math.round(r.percentage)}%` : `${r?.correct ?? 0}/${r?.total ?? 0}`}</span>
            </div>
          ))}
        </div>
      )}
      {strong.length > 0 && <p className="avz-mute avz-mt-4">Fortalezas: {strong.join(", ")}</p>}
      {review.length > 0 && <p className="avz-mute avz-mt-4">Para reforzar: {review.join(", ")}</p>}
    </div>
  );
}

function parentSummary(child) {
  const practicing = child.subjects.filter((s) => s.skills_practiced > 0);
  const hasResults = child.progress_check_last || child.simulation_1_last || child.final_simulation_last;
  if (practicing.length === 0 && !hasResults && !child.sessions_completed) {
    return "Todavía no ha comenzado a practicar. Anímalo(a) a iniciar una sesión corta.";
  }
  const focus = child.subjects.find((s) => s.mastery_label === "Necesita práctica" || s.mastery_label === "En desarrollo");
  if (focus) {
    return `${focus.label} es actualmente una de las áreas donde recomendamos concentrar la práctica.`;
  }
  return "Va avanzando de manera constante en las materias que ha trabajado esta semana.";
}
